import { UserPermission, UserRole } from "helpers";

import { Navigate } from "react-router-dom";
import { lazy } from "react";

const OidcCallback = lazy(() => import("components/oidc/OidcCallback"));
const OidcSilentCallback = lazy(() => import("components/oidc/OidcSilentCallback"));
const NotAuthenticated = lazy(() => import("components/oidc/NotAuthentication"));
const NewDashboard = lazy(() => import("pages/new-dashboard/NewDashboard"));
const Report = lazy(() => import("pages/report/Report"));
const UserAccounts = lazy(() => import("pages/user-accounts/UserAccounts"));
const FarmerAccounts = lazy(
  () => import("pages/farmer-accounts/FarmerAccounts")
);
const FarmerAccountUpdate = lazy(
  () => import("pages/farmer-accounts/FarmerAccountUpdate")
);
const DocumentHub = lazy(() => import("pages/document-hub/DocumentHub"));
const ProducerDocuments = lazy(() => import("pages/producer-documents/ProducerDocumentPage"));
const AlertManagement = lazy(() => import("pages/alert-management/AlertManagement"));
const MySitesProducers = lazy(() => import("pages/my-sites-producers/MySitesProducers"));
const NoRole = lazy(() => import("pages/auth/NoRole"));
const NotFound404 = lazy(() => import("pages/error/NotFound404"));
const ReportDetail = lazy(() => import("pages/report/ReportDetail"));
const WebConfig = lazy(() => import("pages/web-config/WebConfig"));
const Maintenance = lazy(() => import("pages/maintenance/Maintenance"));
const PGAForm = lazy(() => import("pages/PGAForm/PGAForm"));
const PGAFormReview = lazy(() => import("pages/pga-form-review/PGAFormReview"));
const PGAFormEditComponent = lazy(() => import("pages/pga-form-edit/PGAFormEditComponent"));
const CarcaseInfoEmail = lazy(() => import("pages/carcase-info-email/CarcaseInfoEmail"));
const FarmGroups = lazy(() => import("pages/farm-groups/FarmGroups"));
const FASList = lazy(
  () => import("pages/data-management/fas/FASList")
);
const BasePrice = lazy(() => import("pages/base-price/BasePrice"));
const BasePriceAdjustment = lazy(() => import("pages/base-price/BasePriceAdjustment"));
const Bonus = lazy(() => import("pages/pricing/Bonus"));
const BookingsPage = lazy(() => import("pages/bookings/BookingsPage"));
const BookingsRecycleBin = lazy(() => import("pages/bookings/components/BookingsRecycleBin"));
const Charges = lazy(() => import("pages/pricing/Charges"));
const ChargeCircumstances = lazy(() => import("pages/charge-circumstances/ChargeCircumstances"));
const BuyerList = lazy(() => import("pages/data-management/buyers/BuyerList"));
const HaulierList = lazy(() => import("pages/data-management/hauliers/HaulierList"));
const BuyerModify = lazy(() => import("pages/data-management/buyers/BuyerModify"));
const HaulierModify = lazy(() => import("pages/data-management/hauliers/HaulierModify"));
const LotPricingDetails = lazy(() => import("pages/lot-details/LotPricingDetails"));
const LotPricing = lazy(() => import("pages/pricing/LotPricing"));
const LotApprove = lazy(() => import("pages/pricing/LotApprove"));
const Matrices = lazy(() => import("pages/matrices/Matrices"));
const MatrixModify = lazy(() => import("pages/matrices/MatrixModify"));
const ProducerManagement = lazy(() => import("pages/ProducerManagement/ProducerManagement"));
const KKillLineAPILogsPage = lazy(() => import("pages/audit-logs/KillLineAPILogsPage"));
const KillLineAPILogDetailPage = lazy(() => import("pages/audit-logs/KillLineAPILogDetailPage"));
const EmailRecipientsPage = lazy(() => import ("pages/email-recipients/EmailRecipientsPage"));
const TransportCostPerHaulier = lazy(() => import("pages/transport-cost-per-haulier/TransportCostPerHaulier"));
const ManualAdjustments = lazy(() => import("pages/manual-adjustment/ManualAdjustments"));
const EartagSearch = lazy(() => import("pages/eartag-search/EartagSearchPage"));
const BookingFeatures = lazy(() => import("pages/booking-features/BookingFeatures"));

// Roles & Permissions
const RolesPermissionsPage = lazy(() => import("pages/roles-permissions/RolesPermissionsPage"));
const CreateRolePage = lazy(() => import("pages/roles-permissions/CreateRolePage"));
const EditRolePage = lazy(() => import("pages/roles-permissions/EditRolePage"));
const AccountLink = lazy(() => import("pages/account-link/AccountLink"));
const SchemeMembers = lazy(() => import("pages/scheme-members/SchemeMembers"));

const routes: Array<{
  path: string;
  name?: string;
  component: any;
  title?: string;
  exact?: boolean;
  useAuthLayout?: boolean;
  roles?: string[];
  permission?: string;
  needSearchRole?: boolean;
  anyPermissions?: string[];
}> = [
    {
      path: "/auth/callback",
      component: OidcCallback,
      title: "Dashboard",
    },
    {
      path: "/auth/silent_callback",
      component: OidcSilentCallback,
      title: "Dashboard",
    },
    {
      path: "/auth/401",
      component: NotAuthenticated,
      title: "Dashboard",
    },
    {
      path: "/dashboard",
      name: "dashboard",
      component: NewDashboard,
      title: "Dashboard",
      useAuthLayout: true,
      permission: UserPermission.Dashboard,
    },
    {
      path: "/report/detail",
      name: "report-detail",
      component: ReportDetail,
      title: "ReportDetail",
      useAuthLayout: true,
      anyPermissions: [UserPermission.ViewReportslist, UserPermission.ViewAllReportslist],
      needSearchRole: true,
    },
    {
      path: "/report",
      name: "report",
      component: Report,
      title: "Report",
      useAuthLayout: true,
      permission: UserPermission.Report,
      needSearchRole: true,
    },
    {
      path: "/user-accounts",
      name: "user-accounts",
      component: UserAccounts,
      title: "UserAccounts",
      useAuthLayout: true,
      permission: UserPermission.UserAccounts,
    },
    {
      path: "/farmer-accounts",
      name: "farmer-accounts",
      component: FarmerAccounts,
      title: "FarmerAccounts",
      useAuthLayout: true,
      permission: UserPermission.FarmerAccounts,
    },
    {
      path: "/document-hub",
      name: "document-hub",
      component: DocumentHub,
      title: "DocumentHub",
      useAuthLayout: true,
      permission: UserPermission.DocumentHub,
    },
    {
      path: "/eartag-search",
      name: "eartag-search",
      component: EartagSearch,
      title: "Eartag Search",
      useAuthLayout: true,
      permission: UserPermission.EartagSearch,
    },
    {
      path: "/booking-features",
      name: "booking-features",
      component: BookingFeatures,
      title: "Booking Features",
      useAuthLayout: true,
      permission: UserPermission.BookingFeatures_View,
    },
    {
      path: "/producer-documents",
      name: "producer-documents",
      component: ProducerDocuments,
      title: "ProducerDocuments",
      useAuthLayout: true,
      permission: UserPermission.ProducerDocuments,
    },
    {
      path: "/alert-management",
      name: "alert-management",
      component: AlertManagement,
      title: "AlertManagement",
      useAuthLayout: true,
      permission: UserPermission.AlertManagement,
      needSearchRole: true,
    },
    {
      path: "/my-sites-producers",
      name: "my-sites-producers",
      component: MySitesProducers,
      title: "MySitesProducers",
      useAuthLayout: true,
      permission: UserPermission.MySitesProducers,
    },
    {
      path: "/no-role",
      component: NoRole,
      title: "NoRole",
      useAuthLayout: false,
      roles: [UserRole.NoRole],
    },
    {
      path: "/404",
      component: NotFound404,
      title: "NotFound404",
      useAuthLayout: false,
    },
    {
      path: "/web-config",
      name: "WebConfig",
      component: WebConfig,
      title: "WebConfig",
      useAuthLayout: true,
      roles: [UserRole.Admin],
    },
    {
      path: "/pga-form/review",
      name: "pga-form-review",
      component: PGAFormReview,
      title: "PGAFormReview",
      useAuthLayout: true,
      permission: UserPermission.ReviewFlagViewLogsPGA,
    },
    {
      path: "/pga-form/edit",
      name: "pga-form-edit",
      component: PGAFormEditComponent,
      title: "PGAFormEdit",
      useAuthLayout: true,
      permission: UserPermission.EditResetPGA,
    },
    {
      path: "/pga-form",
      name: "PGAForm",
      component: PGAForm,
      title: "PGAForm",
      useAuthLayout: true,
      permission: UserPermission.PGAForms,
    },
    {
      path: "/carcase-info-email",
      name: "carcaseInfoEmail",
      component: CarcaseInfoEmail,
      title: "CarcaseInfoEmail",
      useAuthLayout: true,
      permission: UserPermission.CarcaseInformationEmail,
      needSearchRole: true,
    },
    {
      path: "/benchmarking-groups",
      name: "benchmarkingGroups",
      component: FarmGroups,
      title: "BenchmarkingGroups",
      useAuthLayout: true,
      permission: UserPermission.BenchmarkingGroups,
    },
    {
      path: "/fas",
      name: "fas",
      component: FASList,
      title: "Fas",
      useAuthLayout: true,
      permission: UserPermission.FAS,
    },
    {
      path: "/bookings/recycle-bin",
      name: "bookings-recycle",
      component: BookingsRecycleBin,
      title: "BookingsRecycle",
      useAuthLayout: true,
      permission: UserPermission.Bookings_RecycleList,
    },
    {
      path: "/bookings",
      name: "bookings",
      component: BookingsPage,
      title: "Bookings",
      useAuthLayout: true,
      permission: UserPermission.Bookings,
    },
    {
      path: "/scheme-members",
      name: "scheme-members",
      component: SchemeMembers,
      title: "Manage Scheme Members",
      useAuthLayout: true,
      permission: UserPermission.ManageSchemeMembers,
    },
    {
      path: "/base-price",
      name: "basePrice",
      component: BasePrice,
      title: "Base Price",
      useAuthLayout: true,
      permission: UserPermission.BasePrice,
    },
    {
      path: "/base-price-adjustment",
      name: "basePriceAdjustment",
      component: BasePriceAdjustment,
      title: "Base Price Adjustment",
      useAuthLayout: true,
      permission: UserPermission.BasePriceAdj,
    },
    {
      path: "/matrices/create",
      name: "matrixModify",
      component: MatrixModify,
      title: "MatrixModify",
      useAuthLayout: true,
      permission: UserPermission.Matrix,
    },
    {
      path: "/matrices/modify",
      name: "matrixModify",
      component: MatrixModify,
      title: "MatrixModify",
      useAuthLayout: true,
      permission: UserPermission.Matrix,
    },
    {
      path: "/matrices",
      name: "matrices",
      component: Matrices,
      title: "Matrices",
      useAuthLayout: true,
      permission: UserPermission.Matrix,
    },
    {
      path: "/lot-pricing/detail",
      name: "lotPricing",
      component: LotPricingDetails,
      title: "Lot Pricing",
      useAuthLayout: true,
      permission: UserPermission.LotPricing_ViewDetail,
    },
    {
      path: "/lot-pricing",
      name: "lotPricing",
      component: LotPricing,
      title: "Lot Pricing",
      useAuthLayout: true,
      permission: UserPermission.LotPricing_ViewList,
    },
    {
      path: "/lot-approval",
      name: "lotApproval",
      component: LotApprove,
      title: "Lot Approval",
      useAuthLayout: true,
      permission: UserPermission.GMApproval,
    },
    {
      path: "/charges-circumstances",
      name: "charges-circumstances",
      component: ChargeCircumstances,
      title: "Charge Circumstances",
      useAuthLayout: true,
      permission: UserPermission.Charges,
    },
    {
      path: "/charges",
      name: "charges",
      component: Charges,
      title: "Charges",
      useAuthLayout: true,
      permission: UserPermission.Charges,
    },
    {
      path: "/bonus",
      name: "bonus",
      component: Bonus,
      title: "Bonus",
      useAuthLayout: true,
      permission: UserPermission.Bonus,
    },
    {
      path: "/transport-cost-per-haulier",
      name: "transport-cost-per-haulier",
      component: TransportCostPerHaulier,
      title: "Transport Cost per Haulier",
      useAuthLayout: true,
      permission: UserPermission.TransportCostPerHaulier,
    },
    {
      path: "/manual-adjustment",
      name: "manual-adjustment",
      component: ManualAdjustments,
      title: "Manual Adjustments",
      useAuthLayout: true,
      permission: UserPermission.ManualAdjustment,
    },
    {
      path: "/buyers/modify",
      name: "modify-buyer",
      component: BuyerModify,
      title: "ModifyBuyer",
      useAuthLayout: true,
      permission: UserPermission.Buyer,
    },
    {
      path: "/buyers/create",
      name: "modify-buyer",
      component: BuyerModify,
      title: "ModifyBuyer",
      useAuthLayout: true,
      permission: UserPermission.Buyer,
    },
    {
      path: "/buyers",
      name: "buyers",
      component: BuyerList,
      title: "Buyers",
      useAuthLayout: true,
      permission: UserPermission.Buyer,
    },
    {
      path: "/hauliers/modify",
      name: "modify-hauliers",
      component: HaulierModify,
      title: "HaulierModify",
      useAuthLayout: true,
      permission: UserPermission.Haulier,
    },
    {
      path: "/hauliers/create",
      name: "modify-hauliers",
      component: HaulierModify,
      title: "HaulierModify",
      useAuthLayout: true,
      permission: UserPermission.Haulier,
    },
    {
      path: "/hauliers",
      name: "hauliers",
      component: HaulierList,
      title: "Hauliers",
      useAuthLayout: true,
      permission: UserPermission.Haulier,
    },
    {
      path: "/killine-api-logs",
      name: "killine-api-logs",
      component: KKillLineAPILogsPage,
      title: "KillLineAPILogs",
      useAuthLayout: true,
      permission: UserPermission.KillLineAPILogs,
    },
    {
      path: "/killine-api-logs/:id",
      name: "killine-api-log-detail",
      component: KillLineAPILogDetailPage,
      title: "KillLine API Log Detail",
      useAuthLayout: true,
      permission: UserPermission.KillLineAPILogs,
    },
    {
      path: "/producer-management/update",
      name: "producer-management",
      component: FarmerAccountUpdate,
      title: "FarmerAccountUpdate",
      useAuthLayout: true,
      permission: UserPermission.Producer_ViewListDetail,
    },
    {
      path: "/producer-management",
      name: "producerManagement",
      component: ProducerManagement,
      title: "Producer Management",
      useAuthLayout: true,
      permission: UserPermission.Producer_ViewListDetail,
    },
    {
      path: "/roles-permissions/edit-role/:roleId",
      name: "editRole",
      component: EditRolePage,
      title: "Edit Role",
      useAuthLayout: true,
      permission: UserPermission.ManageRoles,
    },
    {
      path: "/roles-permissions/create-role",
      name: "createRole",
      component: CreateRolePage,
      title: "Create Role",
      useAuthLayout: true,
      permission: UserPermission.ManageRoles,
    },
    {
      path: "/roles-permissions",
      name: "rolesPermissions",
      component: RolesPermissionsPage,
      title: "Roles & Permissions",
      useAuthLayout: true,
      permission: UserPermission.RolePermission,
    },
    {
      path: "/account-link",
      name: "account-link",
      component: AccountLink,
      title: "Linked Accounts Management",
      useAuthLayout: true,
      //permission: UserPermission.LinkedAccount,
    },
    {
      path: "/account-link/:tab",
      name: "account-link",
      component: AccountLink,
      title: "Linked Accounts Management",
      useAuthLayout: true,
      permission: UserPermission.LinkedAccount,
    },
    {
      path: "/email-recipients",
      name: "emailRecipients",
      component: EmailRecipientsPage,
      title: "Email Recipients",
      useAuthLayout: true,
      permission: UserPermission.EmailRecipients,
    },
    {
      path: "/",
      exact: true,
      component: () => <Navigate to="/dashboard" />,
    },
    {
      path: "*",
      name: "notfound404",
      component: NotFound404,
      title: "NotFound404",
      useAuthLayout: false,
    },
  ];

const maintenanceRoutes: Array<{
  path: string;
  name?: string;
  component: any;
  title?: string;
  exact?: boolean;
  useAuthLayout?: boolean;
  roles?: string[];
}> = [
    {
      path: "/web-config",
      name: "WebConfig",
      component: WebConfig,
      title: "WebConfig",
      useAuthLayout: true,
      roles: [UserRole.Admin],
    },
    {
      path: "*",
      name: "maintenance",
      component: Maintenance,
      title: "Maintenance",
      useAuthLayout: false,
    },
  ];

export { routes, maintenanceRoutes };
